import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, asList, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Plus, Wrench, IndianRupee, ArrowLeft, ShieldCheck, Calendar, Loader2, Check,
  Building2, Package, QrCode, TrendingDown, User,
} from "lucide-react";

const DEPTS = ["Casting", "Filing", "Setting", "Polish", "QC", "Tagging", "Dispatch"];
const STATUSES = ["in_use", "idle", "in_repair", "retired", "disposed"];

function Header({ title, eyebrow, actions }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">{eyebrow}</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">{title}</h1>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

// =====================================================================
// ASSETS LIST + CREATE
// =====================================================================
export function AssetsListPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState("all");
  const [dash, setDash] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const canWrite = ["admin", "super-admin", "manager", "accounts"].includes(user?.role);
  const canSeeDash = canWrite;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dept !== "all") params.department = dept;
      const { data } = await api.get("/assets", { params });
      setRows(asList(data));
      if (canSeeDash) {
        try { const { data: d } = await api.get("/assets/dashboard"); setDash(d); } catch (err) { void err; }
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dept]);

  return (
    <div className="space-y-6" data-testid="assets-list-page">
      <Header
        eyebrow="Factory"
        title="Assets"
        actions={canWrite && <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setOpenNew(true)} data-testid="asset-new-btn"><Plus size={14} className="mr-1"/> New Asset</Button>}
      />

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MiniStat label="Total" value={dash.total_assets}/>
          <MiniStat label="Active" value={dash.active_assets}/>
          <MiniStat label="Warranty ≤30d" value={dash.warranties_expiring_30d} accent/>
          <MiniStat label="AMC ≤60d" value={dash.amc_expiring_60d} accent/>
          <MiniStat label="Unpaid" value={`₹${Math.round(dash.payments_outstanding?.amount || 0).toLocaleString("en-IN")}`}/>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Department</Label>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-56" data-testid="assets-dept-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-center py-12 text-[#4A5A50]">Loading…</div> : rows.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-16 text-center">
          <ShieldCheck size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
          <div className="font-serif text-xl">No assets yet</div>
          <div className="text-[#4A5A50] text-sm mt-1">Add machines, tools, and equipment to track warranties, AMC and TCO.</div>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(a => (
            <button key={a.id} onClick={() => nav(`/assets/${a.id}`)} className="text-left bg-white rounded-xl border border-[#E4DFCF] p-5 card-hover" data-testid={`asset-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-serif text-xl truncate">{a.name}</div>
                  {a.category && <div className="text-xs text-[#4A5A50]">{a.category}</div>}
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${["in_use","active"].includes(a.status) ? "bg-[#E1F0E6] text-[#0F5E39]" : a.status === "idle" ? "bg-[#F6EED8] text-[#6E5416]" : a.status === "in_repair" ? "bg-red-50 text-red-700" : "bg-[#F1EDDF] text-[#4A5A50]"}`}>{a.status?.replace("_", " ")}</span>
              </div>
              <div className="mt-3 text-xs text-[#4A5A50] flex items-center gap-1"><Building2 size={12}/> {a.department || "—"}{a.assigned_user_name ? ` · ${a.assigned_user_name}` : ""}</div>
              {a.warranty_expiry && <div className="mt-1 text-xs text-[#4A5A50] flex items-center gap-1"><Calendar size={12}/> Warranty: {format(new Date(a.warranty_expiry), "dd MMM yyyy")}</div>}
              {a.amc_expiry && <div className="text-xs text-[#4A5A50] flex items-center gap-1"><Wrench size={12}/> AMC: {format(new Date(a.amc_expiry), "dd MMM yyyy")}</div>}
            </button>
          ))}
        </div>
      )}

      <AssetFormDialog open={openNew} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }}/>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "bg-[#F6EED8] border-[#C9A24B]/40" : "bg-white border-[#E4DFCF]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      <div className="text-lg font-medium mt-0.5">{value}</div>
    </div>
  );
}

function AssetFormDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", category: "", department: "", assigned_user_id: "", serial_number: "", vendor: "", purchase_date: "", purchase_price: "", useful_life_years: "", salvage_value: "", warranty_expiry: "", amc_expiry: "", status: "in_use", location: "", notes: "" });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (!open) return; setF({ name: "", category: "", department: "", assigned_user_id: "", serial_number: "", vendor: "", purchase_date: "", purchase_price: "", useful_life_years: "", salvage_value: "", warranty_expiry: "", amc_expiry: "", status: "in_use", location: "", notes: "" }); setError(""); (async () => { try { const { data } = await api.get("/users"); setUsers(asList(data)); } catch (err) { void err; } })(); }, [open]);
  const submit = async () => {
    if (!f.name.trim()) { setError("Name required"); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...f };
      ["purchase_date", "warranty_expiry", "amc_expiry"].forEach(k => { payload[k] = payload[k] ? new Date(payload[k]).toISOString() : null; });
      payload.purchase_price = payload.purchase_price === "" ? null : Number(payload.purchase_price);
      payload.useful_life_years = payload.useful_life_years === "" ? null : Number(payload.useful_life_years);
      payload.salvage_value = payload.salvage_value === "" ? null : Number(payload.salvage_value);
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      await api.post("/assets", payload);
      toast.success("Asset added");
      onSaved();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="asset-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Asset</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="asset-name"/></div>
          <div><Label>Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="machine / tool / mould"/></div>
          <div><Label>Department</Label>
            <Select value={f.department || undefined} onValueChange={(v) => setF({ ...f, department: v })}><SelectTrigger data-testid="asset-dept"><SelectValue placeholder="Pick"/></SelectTrigger><SelectContent>{DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="col-span-2"><Label>Assigned to (in-charge)</Label>
            <Select value={f.assigned_user_id || undefined} onValueChange={(v) => setF({ ...f, assigned_user_id: v })}>
              <SelectTrigger data-testid="asset-assignee"><SelectValue placeholder="Optional — pick employee in-charge"/></SelectTrigger>
              <SelectContent className="max-h-64">{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Serial no.</Label><Input value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })}/></div>
          <div><Label>Vendor</Label><Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })}/></div>
          <div><Label>Purchase date</Label><Input type="date" value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })}/></div>
          <div><Label>Purchase price (₹)</Label><Input type="number" step="0.01" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })}/></div>
          <div><Label>Useful life (yrs)</Label><Input type="number" step="0.5" value={f.useful_life_years} onChange={(e) => setF({ ...f, useful_life_years: e.target.value })} data-testid="asset-life"/></div>
          <div><Label>Salvage value (₹)</Label><Input type="number" step="0.01" value={f.salvage_value} onChange={(e) => setF({ ...f, salvage_value: e.target.value })}/></div>
          <div><Label>Warranty expiry</Label><Input type="date" value={f.warranty_expiry} onChange={(e) => setF({ ...f, warranty_expiry: e.target.value })}/></div>
          <div><Label>AMC expiry</Label><Input type="date" value={f.amc_expiry} onChange={(e) => setF({ ...f, amc_expiry: e.target.value })}/></div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })}/></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="asset-submit-btn">{saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// ASSET DETAIL (maintenance + payments + TCO)
// =====================================================================
export function AssetDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tco, setTco] = useState(null);
  const [dep, setDep] = useState(null);
  const [depLoading, setDepLoading] = useState(true);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrSvg, setQrSvg] = useState("");
  const canCost = ["admin", "super-admin", "manager", "accounts"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/assets/${id}`); setA(data);
      const { data: m } = await api.get(`/assets/${id}/maintenance`); setMaintenance(asList(m));
      if (canCost) {
        const { data: p } = await api.get(`/assets/${id}/payments`); setPayments(asList(p));
        const { data: t } = await api.get(`/assets/${id}/tco`); setTco(t);
        setDepLoading(true);
        try {
          const { data: d } = await api.get(`/assets/${id}/depreciation`); setDep(d);
        } finally { setDepLoading(false); }
      } else { setDepLoading(false); }
    } catch { toast.error("Asset not found"); nav("/assets"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const openQR = async () => {
    try {
      const res = await api.get(`/assets/${id}/qr`, { responseType: "text" });
      setQrSvg(typeof res.data === "string" ? res.data : "");
      setQrOpen(true);
    } catch { toast.error("QR generation failed"); }
  };
  const printQR = () => {
    if (!qrSvg) return;
    const w = window.open("", "_blank", "width=400,height=500");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Asset QR · ${a?.name || ""}</title>
      <style>body{font-family:sans-serif;text-align:center;margin:24px}h2{margin:0 0 4px}small{color:#4a5a50}svg{width:280px;height:280px;margin:16px 0}</style>
      </head><body><h2>${a?.name || ""}</h2><small>${a?.serial_number || ""} · ${a?.department || ""}</small>${qrSvg}<div><small>Scan to open asset in TARU</small></div><script>window.onload=()=>window.print();</script></body></html>`);
    w.document.close();
  };

  if (!a) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="asset-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <Header eyebrow="Asset" title={a.name} actions={
        <Button variant="outline" className="rounded-full h-11" onClick={openQR} data-testid="asset-qr-btn"><QrCode size={14} className="mr-1"/> QR tag</Button>
      }/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-4 space-y-2 text-sm">
          <Row k="Category" v={a.category}/>
          <Row k="Department" v={a.department}/>
          {a.assigned_user_name && <Row k="Assigned to" v={<span className="inline-flex items-center gap-1"><User size={12}/> {a.assigned_user_name}</span>}/>}
          <Row k="Serial" v={a.serial_number}/>
          <Row k="Status" v={<span className="capitalize">{a.status?.replace("_"," ")}</span>}/>
          {canCost && <Row k="Vendor" v={a.vendor}/>}
          {canCost && a.purchase_price != null && <Row k="Purchase price" v={`₹ ${Number(a.purchase_price).toLocaleString("en-IN")}`}/>}
          {canCost && a.useful_life_years != null && <Row k="Useful life" v={`${a.useful_life_years} yrs`}/>}
          {canCost && a.salvage_value != null && <Row k="Salvage" v={`₹ ${Number(a.salvage_value).toLocaleString("en-IN")}`}/>}
          {a.warranty_expiry && <Row k="Warranty" v={format(new Date(a.warranty_expiry), "dd MMM yyyy")}/>}
          {a.amc_expiry && <Row k="AMC" v={format(new Date(a.amc_expiry), "dd MMM yyyy")}/>}
          {a.notes && <Row k="Notes" v={a.notes}/>}
        </CardContent></Card>
        {tco && (
          <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Total Cost of Ownership</div>
            <div className="mt-2 font-serif text-3xl">₹ {Math.round(tco.tco || 0).toLocaleString("en-IN")}</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-[#4A5A50]">
              <div>Purchase: <strong className="text-[#0F1E15]">₹ {Math.round(tco.purchase_price || 0).toLocaleString("en-IN")}</strong></div>
              <div>Payments: <strong className="text-[#0F1E15]">₹ {Math.round(tco.payments_total || 0).toLocaleString("en-IN")}</strong></div>
              <div>Maintenance: <strong className="text-[#0F1E15]">₹ {Math.round(tco.maintenance_total || 0).toLocaleString("en-IN")}</strong></div>
            </div>
          </CardContent></Card>
        )}
      </div>

      {canCost && depLoading && (
        <Card className="border-[#E4DFCF] rounded-xl" data-testid="asset-depreciation-skeleton"><CardContent className="p-4 animate-pulse">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#4A5A50] mb-3"><TrendingDown size={12}/> Depreciation · Straight line</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[0,1,2,3].map(i => <div key={i} className="h-16 bg-[#F1EDDF] rounded-lg"/>)}
          </div>
          <div className="mt-4 space-y-2">{[0,1,2].map(i => <div key={i} className="h-6 bg-[#F1EDDF] rounded"/>)}</div>
        </CardContent></Card>
      )}
      {canCost && !depLoading && dep && dep.configured && (
        <Card className="border-[#E4DFCF] rounded-xl" data-testid="asset-depreciation-card"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2"><TrendingDown size={12}/> Depreciation · Straight line</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Book value" value={`₹ ${Math.round(dep.book_value).toLocaleString("en-IN")}`} accent/>
            <MiniStat label="Accumulated" value={`₹ ${Math.round(dep.accumulated_depreciation).toLocaleString("en-IN")}`}/>
            <MiniStat label="Annual" value={`₹ ${Math.round(dep.annual_depreciation).toLocaleString("en-IN")}`}/>
            <MiniStat label="Remaining" value={`${dep.remaining_life_years} yrs`}/>
          </div>
          {dep.schedule?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-[#4A5A50]"><tr>
                  <th className="text-left p-2">Year</th><th className="text-right p-2">Depreciation</th><th className="text-right p-2">Accumulated</th><th className="text-right p-2">Book value</th>
                </tr></thead>
                <tbody>{dep.schedule.map(s => (
                  <tr key={s.year} className="border-t border-[#E4DFCF]">
                    <td className="p-2">Yr {s.year}</td>
                    <td className="p-2 text-right">₹ {s.depreciation.toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right">₹ {s.accumulated.toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right">₹ {s.book_value.toLocaleString("en-IN")}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </CardContent></Card>
      )}
      {canCost && !depLoading && dep && !dep.configured && (
        <Card className="border-amber-200 bg-amber-50/40 rounded-xl"><CardContent className="p-4 text-sm text-amber-900">
          <div className="font-medium">Depreciation not configured</div>
          <div className="text-xs mt-1">Set <b>purchase price</b>, <b>useful life (years)</b> and <b>purchase date</b> on the asset to compute a straight-line schedule.</div>
        </CardContent></Card>
      )}

      <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">Maintenance log</div>
        {maintenance.length === 0 ? <div className="text-sm text-[#4A5A50]">No entries yet.</div> : (
          <ul className="divide-y divide-[#E4DFCF]">
            {maintenance.map(m => (
              <li key={m.id} className="py-2 flex justify-between gap-3">
                <div>
                  <div className="text-sm">{m.description}</div>
                  <div className="text-xs text-[#4A5A50]">{m.date ? format(new Date(m.date), "dd MMM yyyy") : "—"} · {m.created_by_name || "—"}</div>
                </div>
                {canCost && m.cost != null && <div className="text-sm font-medium">₹ {Math.round(m.cost).toLocaleString("en-IN")}</div>}
              </li>
            ))}
          </ul>
        )}
      </CardContent></Card>
      {canCost && (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">Payments</div>
          {payments.length === 0 ? <div className="text-sm text-[#4A5A50]">No payments scheduled.</div> : (
            <ul className="divide-y divide-[#E4DFCF]">
              {payments.map(p => (
                <li key={p.id} className="py-2 flex justify-between gap-3 text-sm">
                  <div>
                    <div>{p.kind} · {p.due_date ? format(new Date(p.due_date), "dd MMM yyyy") : "—"}</div>
                    {p.reference && <div className="text-xs text-[#4A5A50]">Ref: {p.reference}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-medium">₹ {Math.round(p.amount || 0).toLocaleString("en-IN")}</div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${p.paid ? "bg-[#E1F0E6] text-[#0F5E39]" : "bg-[#FCF7EA] text-[#7A6329]"}`}>{p.paid ? "paid" : "due"}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      )}

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="asset-qr-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">QR tag · {a.name}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3">
            <div className="w-64 h-64 flex items-center justify-center bg-white border border-[#E4DFCF] rounded-xl p-4" dangerouslySetInnerHTML={{ __html: qrSvg }}/>
            <div className="text-xs text-[#4A5A50] text-center">Print &amp; stick this on the machine. Scanning opens the asset in TARU.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQrOpen(false)}>Close</Button>
            <Button className="bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={printQR} data-testid="asset-qr-print"><QrCode size={14} className="mr-1"/> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (<div className="flex justify-between gap-3"><span className="text-[#4A5A50] text-xs uppercase tracking-widest">{k}</span><span>{v || "—"}</span></div>);
}
